<template>
    <div class="dingzhi-list">
        <div class="page-title">
            我的定制需求
            <el-button type="primary" size="small" @click="goPublish">+ 发布定制需求</el-button>
        </div>

        <!-- 搜索 + 筛选 -->
        <div class="header">
            <el-input v-model="query.keyword" size="small" placeholder="按标题搜索" clearable
                style="width:200px" @keyup.enter.native="search" @clear="search"></el-input>
            <el-select v-model="query.auditStatus" size="small" placeholder="审核状态" clearable style="width:130px" @change="search">
                <el-option label="待审核" :value="0" />
                <el-option label="已通过" :value="1" />
                <el-option label="已拒绝" :value="2" />
            </el-select>
            <el-button size="small" type="primary" @click="search">查询</el-button>
            <span class="refresh-btn" @click="search">刷新</span>
        </div>

        <el-table :data="list" v-loading="loading" style="width: 100%" empty-text="暂无定制需求">
            <el-table-column prop="title" label="定制软件标题" min-width="160" show-overflow-tooltip></el-table-column>
            <el-table-column label="定制模式" width="110">
                <template slot-scope="{ row }">
                    <el-tag size="small" :type="row.mode === 'crowdfunding' ? 'warning' : 'info'">{{ modeText(row.mode) }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="demand" label="需求详细说明" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column label="预算金额" width="120">
                <template slot-scope="{ row }">
                    <span style="color:#e6a23c">{{ formatBudget(row.budget) }}</span>
                </template>
            </el-table-column>
            <el-table-column label="预算补充说明" min-width="160" show-overflow-tooltip>
                <template slot-scope="{ row }">{{ row.budgetRemark || '-' }}</template>
            </el-table-column>
            <el-table-column label="附件" width="90">
                <template slot-scope="{ row }">
                    <el-tag size="small" :type="fileCount(row) ? '' : 'info'">{{ fileCount(row) }} 个</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="审核状态" width="100">
                <template slot-scope="{ row }">
                    <el-tag size="small" :type="auditType(row)">{{ auditText(row) }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="createTime" label="创建时间" width="160"></el-table-column>
            <el-table-column label="操作" width="100" fixed="right">
                <template slot-scope="{ row }">
                    <el-button type="text" size="mini" style="color:#409EFF;padding-left:0" @click="viewDetail(row)">查看详情</el-button>
                </template>
            </el-table-column>
        </el-table>

        <el-pagination
            v-if="total > 0"
            class="pager"
            background
            layout="total, prev, pager, next"
            :total="total"
            :page-size="query.pageSize"
            :current-page="query.pageNum"
            @current-change="handlePage">
        </el-pagination>

        <!-- 详情弹窗（独立组件 detail.vue，含投标列表 + 选标） -->
        <custom-need-detail :visible.sync="detailVisible" :detail="detail" />

        <!-- 发布定制需求弹窗：内嵌 form.vue -->
        <el-dialog title="发布定制需求" :visible.sync="publishVisible" width="720px" append-to-body
            @closed="publishVisible = false">
            <custom-need-form v-if="publishVisible" @success="onPublishSuccess" />
        </el-dialog>
    </div>
</template>

<script>
import { softwareCustomDemandMyList, softwareCustomDemandDetail } from '@/api/software'
import CustomNeedForm from './form.vue'
import CustomNeedDetail from './detail.vue'

export default {
    components: { CustomNeedForm, CustomNeedDetail },
    data() {
        return {
            loading: false,
            list: [],
            total: 0,
            query: {
                pageNum: 1,
                pageSize: 10,
                keyword: '',
                auditStatus: '',
            },
            detailVisible: false,
            detail: null,
            publishVisible: false, // 发布定制需求弹窗
        }
    },
    created() {
        this.getList()
    },
    methods: {
        async getList() {
            this.loading = true
            try {
                // GET 接口 /stage-api/software/custom/demand/my/list 的分页参数（int32，必需）
                const params = {
                    pageNum: Number(this.query.pageNum) || 1, // 查询页码，缺省取第 1 页
                    pageSize: Number(this.query.pageSize) || 10, // 每页条数，缺省 10
                }
                if (this.query.keyword) params.keyword = this.query.keyword
                // 后端审核状态字段名为 status，查询参数也用 status
                if (this.query.auditStatus !== '' && this.query.auditStatus !== null && this.query.auditStatus !== undefined) {
                    params.status = this.query.auditStatus
                }
                const res = await softwareCustomDemandMyList(params)
                const base = res && res.data !== undefined ? res.data : res
                const rows = base.rows || base.list || (Array.isArray(base) ? base : [])
                this.list = Array.isArray(rows) ? rows : []
                this.total = base.total || (base.data && base.data.total) || this.list.length
            } catch (e) {
                this.list = []
                this.total = 0
            } finally {
                this.loading = false
            }
        },
        search() {
            this.query.pageNum = 1
            if (this.detailVisible) this.detailVisible = false
            this.getList()
        },
        handlePage(page) {
            this.query.pageNum = page
            this.getList()
        },
        viewDetail(row) {
            // 先打开弹窗，用列表行兜底显示（避免接口慢/失败时无内容）
            this.detailVisible = true
            const fallback = row ? { ...row } : null
            this.detail = fallback
            // 调用详情接口 /stage-api/software/custom/demand/{id} 获取完整数据
            const id = row && (row.id !== undefined && row.id !== null && row.id !== '' ? row.id : row.customId)
            if (id === undefined || id === null || id === '') {
                return
            }
            softwareCustomDemandDetail(id).then(res => {
                const data = res && res.data !== undefined ? res.data : res
                if (data && typeof data === 'object') {
                    // 合并：详情优先，列表行兜底字段补充（防止详情缺字段）
                    this.detail = { ...fallback, ...data, id: data.id !== undefined ? data.id : fallback.id }
                }
            }).catch(err => {
                console.warn('定制需求详情加载失败，使用列表行数据', err)
                // 失败保持兜底数据，弹窗仍可看
            })
        },
        goPublish() {
            this.publishVisible = true
        },
        // 弹窗内表单提交成功后：刷新列表并关闭弹窗
        onPublishSuccess() {
            this.publishVisible = false
            this.search()
        },
        // 定制模式：personal-个人定制 / crowdfunding-多人众筹
        modeText(mode) {
            if (mode === 'crowdfunding') return '多人众筹'
            if (mode === 'personal') return '个人定制'
            return mode || '-'
        },
        // 审核状态字段名：后端实体字段为 status（0待审核 1已通过 2已拒绝）。
        // 取值规范化：'1'、1、'已通过' 等都能正确识别为对应状态。
        auditValue(row) {
            if (!row || typeof row !== 'object') return ''
            const raw = row.status
            if (raw === '' || raw === null || raw === undefined) return ''
            // 字符串状态直接匹配中文
            if (typeof raw === 'string') {
                const t = raw.trim()
                if (['1', '已通过', '通过'].includes(t)) return 1
                if (['2', '已拒绝', '拒绝'].includes(t)) return 2
                if (['0', '待审核', '待审', '未审核'].includes(t)) return 0
                // 已是数字字符串，直接返回数值
                const n = Number(t)
                if (!isNaN(n)) return n
                return ''
            }
            return raw
        },
        // 审核状态：0待审核 1已通过 2已拒绝
        auditText(row) {
            const s = this.auditValue(row)
            if (s === 0) return '待审核'
            if (s === 1) return '已通过'
            if (s === 2) return '已拒绝'
            return '待审核'
        },
        auditType(row) {
            const s = this.auditValue(row)
            if (s === 1) return 'success'
            if (s === 2) return 'danger'
            return 'warning'
        },
        formatBudget(b) {
            if (b === '' || b === null || b === undefined) return '-'
            if (typeof b === 'object') return '-' // 防止 budget 为对象/数组时渲染异常
            const n = Number(b)
            return isNaN(n) ? String(b) : n + ' 元'
        },
        // demandFiles / demandFile / attachments 可能为 array[string] / 逗号分隔字符串 / 单个对象 / 对象数组
        fileList(demandFile) {
            if (Array.isArray(demandFile)) return demandFile.filter(f => f && (typeof f === 'object' ? (f.url || f.name) : String(f).trim()))
            if (typeof demandFile === 'string' && demandFile.trim()) return demandFile.split(',').filter(f => f && f.trim())
            if (demandFile && typeof demandFile === 'object') return [demandFile] // 单个对象
            return []
        },
        fileCount(row) {
            if (!row) return 0
            const files = row.demandFiles || row.demandFile || row.attachments
            return this.fileList(files).length
        },
    },
}
</script>

<style lang="scss" scoped>
.dingzhi-list {
    padding: 16px;
    .page-title {
        font-size: 18px;
        font-weight: bold;
        margin-bottom: 16px;
        color: #333;
        display: flex;
        align-items: center;
        justify-content: space-between;
    }
    .header {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
        margin-bottom: 16px;
        .refresh-btn {
            color: #409eff;
            cursor: pointer;
            font-size: 13px;
            user-select: none;
        }
    }
    .pager {
        margin-top: 16px;
        text-align: right;
    }
    .file-item {
        line-height: 1.8;
    }
    .file-link {
        color: #409eff;
        text-decoration: underline;
        word-break: break-all;
    }
}
</style>
